import React, { useState } from "react";
import { Package, Search as SearchIcon, Truck, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { TrackingResponse } from "@/services/types/domain";
// Ajusta a tu servicio real:
import { ordersApi } from "@/services/api/orders"; // .trackOrder({ order_number, email })



const statusLabel = (s: string) => ({
    created: "Creado",
    processing: "Procesando",
    shipped: "Enviado",
    in_transit: "En tránsito",
    out_for_delivery: "Saliendo a entrega",
    delivered: "Entregado",
    cancelled: "Cancelado",
} as Record<string, string>)[s] || s;

const statusVariant = (s: string): Parameters<typeof Badge>[0]["variant"] =>
    s === "delivered" ? "success" : s === "cancelled" ? "destructive" : "secondary";

const OrderTrackingPage: React.FC = () => {
    const [orderNumber, setOrderNumber] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<TrackingResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    const track = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSummary(null);
        if (!orderNumber || !email) {
            setError("Ingresa el número de pedido y tu correo.");
            return;
        }
        try {
            setLoading(true);
            const res = await ordersApi.trackOrder({ order_number: orderNumber, email });
            console.log(res);
            const data = res
            //const data = res
            setSummary(data);
            if (!data?.tracking_history?.length) {
                toast("Sin eventos aún. Vuelve a intentar más tarde.");
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "No se pudo rastrear tu pedido";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 px-0 py-6">
            <div className="mx-auto w-full max-w-4xl">
                <div className="mb-6 text-center">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Rastreo de pedido</h1>
                    <p className="text-gray-600">Ingresa tu número de pedido y correo para ver el estado</p>
                </div>

                <form onSubmit={track} className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                            label="Número de pedido"
                            value={orderNumber}
                            onChange={(e: any) => setOrderNumber(e.target.value)}
                            placeholder="Ej. ORD-68A7C488C4FDD"
                            leftIcon={<Package className="w-5 h-5" />}
                            fullWidth
                        />
                        <Input
                            label="Correo"
                            type="email"
                            value={email}
                            onChange={(e: any) => setEmail(e.target.value)}
                            placeholder="tu@email.com"
                            leftIcon={<SearchIcon className="w-5 h-5" />}
                            fullWidth
                        />
                    </div>
                    {error && <div className="text-sm text-red-600">{error}</div>}
                    <div className="flex justify-end">
                        <Button type="submit" disabled={loading}>{loading ? "Buscando…" : "Rastrear"}</Button>
                    </div>
                </form>

                {summary ? (
                    <div className="mt-6 space-y-4">
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <Truck className="w-5 h-5 text-primary-600" />
                                    <div className="font-semibold text-gray-900">Estado</div>
                                </div>
                                <Badge variant={statusVariant(summary.status)}>{statusLabel(summary.status)}</Badge>
                            </div>
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-700">
                                {/* <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" /> ETA <span className="font-medium">{summary.eta ? new Date(summary.eta).toLocaleString() : "—"}</span></div> */}
                                {/* <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /> Dirección <span className="font-medium">{summary.address || "—"}</span></div> */}
                                <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-gray-400" /> Número <span className="font-medium">{orderNumber}</span></div>
                            </div>
                        </div>

                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 font-semibold text-gray-900">Historial</div>
                            <ol className="relative border-s-2 border-gray-200 ml-3">
                                {summary.tracking_history.map((ev, idx) => (
                                    <li key={idx} className="mb-6 ms-4">
                                        <span className="absolute -start-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 ring-2 ring-white">
                                            <span className="h-2.5 w-2.5 rounded-full bg-primary-600" />
                                        </span>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant={statusVariant(ev.status)}>{statusLabel(ev.status)}</Badge>
                                            <span className="text-xs text-gray-500">{new Date(ev.timestamp).toLocaleString()}</span>
                                        </div>
                                        <div className="mt-1 text-gray-700">{ev.notes || "—"}</div>
                                        {ev.location && (<div className="text-xs text-gray-500">{ev.location}</div>)}
                                    </li>
                                ))}
                                {summary.tracking_history.length === 0 && (
                                    <div className="text-sm text-gray-600 pl-2">Sin eventos aún.</div>
                                )}
                            </ol>
                        </div>
                    </div>
                ) : (
                    !loading && (
                        <div className="mt-6 rounded-2xl border bg-white p-4 sm:p-6 shadow-sm text-sm text-gray-600 flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-gray-400" />
                            Ingresa el número de pedido que recibiste por correo al realizar la compra.
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default OrderTrackingPage;
